import { KeyValuePair } from '@acp/core-ui';
import { ContactNumber, formatContactNumber, getReadableContactNumberType } from '@acp/shared';
import {
    Badge,
    Box,
    Card, CardBody, Grid, GridItem, HStack, Text,
    VStack,
} from '@chakra-ui/react';

const COLORED_BORDER_WIDTH = 6;
const CARD_BORDER_PADDING = 5;

function GridWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Grid
            gridTemplateRows="auto"
            gridTemplateAreas={{
                base: `
                    "nameEmail nameEmail"
                    "contactNumbers contactNumbers"
                `,
                md: `
                    "nameEmail contactNumbers"
                `,
            }}
            gridTemplateColumns={{
                base: '1fr',
                md: '2fr 1fr',
            }}
            gap={4}
            alignItems="center"
            justifyItems="start"
            h="auto"
        >
            {children}
        </Grid>
    );
}

function OccupantsTableHeader() {
    return (
        <Box
            borderBottomWidth={1}
            borderLeftWidth={COLORED_BORDER_WIDTH}
            px={CARD_BORDER_PADDING}
            pb={2}
            fontSize={{ base: 12, lg: 13 }}
            w="full"
            borderLeftColor="transparent"
        >
            <GridWrapper>
                <GridItem area="nameEmail">Name</GridItem>
                <GridItem area="email">Email</GridItem>
                <GridItem area="contactNumbers">Contact numbers</GridItem>
            </GridWrapper>
        </Box>
    );
}

function OccupantContactNumber({
    contactNumber,
    isBlacklisted,
}: {
    isBlacklisted: boolean;
    contactNumber: ContactNumber;
}) {
    return (
        <VStack align="start" gap={1}>
            <HStack gap={2}>
                <Text>{formatContactNumber(contactNumber.number)}</Text>
                <Text color="muted.500">
                    {`(${getReadableContactNumberType(contactNumber.type)})`}
                </Text>
            </HStack>
            { isBlacklisted
            && contactNumber.doNotContactReasonName !== null
            && (
                <Badge colorScheme="black" variant="solid" size="sm">
                    {
                        contactNumber.doNotContactReasonCode === 'OTHER'
                            ? 'Unable to contact'
                            : contactNumber.doNotContactReasonName
                    }
                </Badge>
            )}
        </VStack>
    );
}

function OccupantRow({
    name,
    email,
    isBlacklisted,
    contactNumbers,
}: {
    name: string | null;
    email: string | null;
    isBlacklisted: boolean;
    contactNumbers: ContactNumber[];
}) {
    return (
        <Card
            borderColor="muted.50"
            borderWidth={1}
            alignSelf="flex-start"
            bg="white"
            variant="filled"
            w="full"
            shadow="sm"
            p={5}
        >
            <CardBody
                as={GridWrapper}
                p={0}
                alignItems="center"
                justifyItems="center"
            >
                <GridItem area="nameEmail" alignSelf="start" justifySelf="start">
                    <VStack alignItems="flex-start">
                        <Text fontWeight="bold" alignSelf="flex-start">{name}</Text>

                        {email !== null && (
                            <Text fontWeight="normal">{email}</Text>
                        )}

                        {email === null && (
                            <Text color="muted.300">Email not specified</Text>
                        )}
                    </VStack>
                </GridItem>
                <GridItem area="contactNumbers">
                    <KeyValuePair label="Contact numbers">
                        {
                            contactNumbers.length > 0
                                ? contactNumbers.map((contactNumber, id) => (
                                    <OccupantContactNumber
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={id}
                                        contactNumber={contactNumber}
                                        isBlacklisted={isBlacklisted}
                                    />
                                ))
                                : <Text color="muted.300">None</Text>
                        }
                    </KeyValuePair>
                </GridItem>
            </CardBody>
        </Card>
    );
}

export { OccupantRow, OccupantsTableHeader };
